/**
 * Утилиты для SEO оптимизации
 */

export const SITE_URL = "https://www.quantilecamp.ru";
export const SITE_NAME = "Quantile Camp";
export const DEFAULT_TITLE = "Quantile Camp - SQL тренажёр для ClickHouse";
export const DEFAULT_DESCRIPTION = "Интерактивный SQL тренажёр для изучения ClickHouse. Практикуйтесь на реальных задачах, изучайте курсы и улучшайте свои навыки работы с данными.";
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.svg`;

export interface SEOData {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "course";
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
}

/**
 * Генерирует полный title для страницы
 */
export function generateTitle(pageTitle?: string): string {
  if (!pageTitle) return DEFAULT_TITLE;
  return `${pageTitle} | ${SITE_NAME}`;
}

/**
 * Генерирует канонический URL
 */
export function generateCanonicalUrl(path: string): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
}

/**
 * Генерирует OG изображение URL
 */
export function generateOGImage(image?: string): string {
  return image ? (image.startsWith("http") ? image : `${SITE_URL}${image}`) : DEFAULT_IMAGE;
}

/**
 * Генерирует структурированные данные для Organization
 */
export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": SITE_NAME,
    "url": SITE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": `${SITE_URL}/favicon.svg`,
      "width": 100,
      "height": 100
    },
    "description": DEFAULT_DESCRIPTION,
    "foundingDate": "2024",
    "founder": {
      "@type": "Person",
      "name": "Валентин Борисов"
    },
    "sameAs": [
      "https://t.me/quantilecamp"
    ],
    "knowsAbout": [
      "ClickHouse",
      "SQL",
      "Column-oriented databases",
      "OLAP",
      "Data Engineering",
      "Big Data Analytics",
      "Database Management Systems",
      "Data Analytics"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "availableLanguage": ["Russian"]
    },
    // Информация о регионе для Yandex Webmaster
    "areaServed": {
      "@type": "Country",
      "name": "Россия",
      "alternateName": "Russia"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "RU"
    }
  };
}

/**
 * Генерирует структурированные данные для WebSite
 */
export function generateWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "url": SITE_URL,
    "description": DEFAULT_DESCRIPTION,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/simulator?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
}

/**
 * Генерирует структурированные данные для Course
 */
export function generateCourseSchema(course: {
  title: string;
  description: string;
  url: string;
  provider?: string;
  level?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": course.title,
    "description": course.description,
    "url": course.url,
    "provider": {
      "@type": "EducationalOrganization",
      "name": course.provider || SITE_NAME,
      "url": SITE_URL
    },
    "educationalLevel": course.level || "Beginner",
    "inLanguage": "ru",
    "courseCode": course.title.toLowerCase().replace(/\s+/g, "-"),
    "teaches": [
      "ClickHouse",
      "SQL",
      "Database Management",
      "Data Analytics",
      "Big Data"
    ],
    "coursePrerequisites": {
      "@type": "Course",
      "name": "Базовые знания SQL"
    },
    "isAccessibleForFree": true,
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "inLanguage": "ru"
    }
  };
}

/**
 * Генерирует структурированные данные для Article (урок)
 */
export function generateArticleSchema(article: {
  title: string;
  description: string;
  url: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "url": article.url,
    "datePublished": article.publishedTime || new Date().toISOString(),
    "dateModified": article.modifiedTime || article.publishedTime || new Date().toISOString(),
    "author": {
      "@type": "Person",
      "name": article.author || "Валентин Борисов",
      "jobTitle": "Data Engineer & Educator"
    },
    "publisher": {
      "@type": "EducationalOrganization",
      "name": SITE_NAME,
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/favicon.svg`,
        "width": 100,
        "height": 100
      }
    },
    "image": article.image || DEFAULT_IMAGE,
    "inLanguage": "ru",
    "articleSection": "Education",
    "keywords": "ClickHouse, SQL, обучение, базы данных"
  };
}

/**
 * Генерирует структурированные данные для BreadcrumbList
 */
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`
    }))
  };
}

/**
 * Генерирует структурированные данные для FAQPage
 */
export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}
